import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import TabGroup from "@/components/ui/tab";
import Dropdown from "@/components/ui/dropdown";
import Button from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Plus } from "@/components/icons";
import { renderPaginationNumbers } from "@/utils/pagination";
import type { FilterField } from "@/types/component";
import type { Certificate, CertificateTab } from "../types/certificates.types";
import {
  certificatesApi,
  EducationCertificateTemplateStatus,
} from "../services/certificates.api";
import CertificateNumberSettingsModal from "../components/CertificateNumberSettingsModal";
import { generateAndDownloadCertificatePDFFromHtml } from "../utils/certificate-pdf-generator";
import CertificateCard from "@/features/client-certificates/components/CertificateCard";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./CertificatesPage.module.scss";

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SEARCH_DEBOUNCE_MS = 300;

const TABS: { label: string; value: CertificateTab }[] = [
  { label: "certificates.tabs.assigned", value: "assigned" },
  { label: "certificates.tabs.unassigned", value: "unassigned" },
  { label: "certificates.tabs.draft", value: "draft" },
  { label: "certificates.tabs.archived", value: "archived" },
];

const TAB_STATUS_MAP: Record<
  CertificateTab,
  EducationCertificateTemplateStatus
> = {
  assigned: EducationCertificateTemplateStatus.ASSIGNED,
  unassigned: EducationCertificateTemplateStatus.UNASSIGNED,
  draft: EducationCertificateTemplateStatus.DRAFT,
  archived: EducationCertificateTemplateStatus.ARCHIVE,
};

type SortByOption = "name" | "createdAt" | "updatedAt" | "status";
type SortOrderOption = "asc" | "desc";

const SORT_OPTIONS: { value: string; sortBy: SortByOption; sortOrder: SortOrderOption }[] = [
  { value: "createdAt-desc", sortBy: "createdAt", sortOrder: "desc" },
  { value: "createdAt-asc", sortBy: "createdAt", sortOrder: "asc" },
  { value: "name-asc", sortBy: "name", sortOrder: "asc" },
  { value: "name-desc", sortBy: "name", sortOrder: "desc" },
  { value: "updatedAt-desc", sortBy: "updatedAt", sortOrder: "desc" },
  { value: "updatedAt-asc", sortBy: "updatedAt", sortOrder: "asc" },
  { value: "status-asc", sortBy: "status", sortOrder: "asc" },
  { value: "status-desc", sortBy: "status", sortOrder: "desc" },
];

function getSortLabel(opt: { sortBy: SortByOption; sortOrder: SortOrderOption }): string {
  const by =
    opt.sortBy === "name"
      ? i18n.t("certificatesPageSort.byName")
      : opt.sortBy === "createdAt"
        ? i18n.t("certificatesPageSort.byDate")
        : opt.sortBy === "updatedAt"
          ? i18n.t("certificatesPageSort.byUpdate")
          : i18n.t("certificatesPageSort.byStatus");
  const order = opt.sortOrder === "desc"
    ? i18n.t("certificatesPageSort.descSuffix")
    : i18n.t("certificatesPageSort.ascSuffix");
  return by + order;
}

const CertificatesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.CERTIFICATES_CREATE);
  const canViewDomainSettings = hasPermission(PERMISSIONS.DOMAIN_SETTINGS_VIEW);
  const canUpdateDomainSettings = hasPermission(PERMISSIONS.DOMAIN_SETTINGS_UPDATE);

  const [activeTab, setActiveTab] = useState<CertificateTab>("assigned");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, setFilterValues] = useState<Record<string, unknown>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortByOption>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrderOption>("desc");
  const [isNumberSettingsOpen, setIsNumberSettingsOpen] = useState(false);

  const filterFields: FilterField[] = [
    // {
    //   name: "dateRange",
    //   label: t("certificates.filters.dateRange"),
    //   type: "dropdown",
    //   placeholder: t("certificates.filters.dateRangePlaceholder"),
    //   options: [
    //     { label: "Son 1 Ay", value: "1m" },
    //     { label: "Son 3 Ay", value: "3m" },
    //     { label: "Son 6 Ay", value: "6m" },
    //   ],
    // },
  ];

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(searchInputValue);
      setCurrentPage(1);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInputValue]);

  useEffect(() => {
    let isMounted = true;
    const fetchCertificates = async () => {
      setLoading(true);
      try {
        const { data, total } = await certificatesApi.getAdminCertificates({
          page: currentPage,
          limit: pageSize,
          status: TAB_STATUS_MAP[activeTab],
          search: searchTerm.trim() || undefined,
          sortBy,
          sortOrder,
        });
        if (!isMounted) return;
        setCertificates(data.map((c) => ({ ...c, status: activeTab })));
        setTotalRecords(total);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCertificates();

    return () => {
      isMounted = false;
    };
  }, [activeTab, currentPage, pageSize, searchTerm, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const currentSortValue =
    SORT_OPTIONS.find((o) => o.sortBy === sortBy && o.sortOrder === sortOrder)?.value ??
    "createdAt-desc";

  const handleSortChange = (value: string) => {
    const option = SORT_OPTIONS.find((o) => o.value === value);
    if (option) {
      setSortBy(option.sortBy);
      setSortOrder(option.sortOrder);
      setCurrentPage(1);
    }
  };

  const handleFilterChange = (filters: Record<string, unknown>) =>
    setFilterValues(filters);

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handleTabChange = (index: number) => {
    setActiveTab(TABS[index].value);
    setCurrentPage(1);
    setSearchInputValue("");
    setSearchTerm("");
  };

  const handleCertificateAction = async (
    certificate: Certificate,
    action: string,
  ) => {
    if (action === "view") {
      navigate(`/dashboard/certificates/${certificate.id}`);
    } else if (action === "download") {
      if (!certificate.rawHtml?.trim()) {
        toast.error(
          t("certificates.download.noContent"),
        );
        return;
      }
      try {
        const filename = `sertifika-${certificate.title?.replace(/\s+/g, "-") || certificate.id}-${Date.now()}`;
        await generateAndDownloadCertificatePDFFromHtml(
          certificate.rawHtml,
          filename,
        );
        toast.success(
          t("certificates.download.success"),
        );
      } catch {
        toast.error(
          t("certificates.download.error"),
        );
      }
    } else if (action === "share") {
      console.log("Share certificate:", certificate.id);
    }
  };

  const handleAddCertificate = () => {
    navigate("/dashboard/certificates/create");
  };

  return (
    <DashboardLayout>
      <Header title={t("certificates.title")} />

      <div className={styles.pageContainer}>
        <div className={styles.tabsWrapper}>
          <TabGroup
            tabs={TABS.map((tab) => ({
              label: t(tab.label, tab.value),
              content: null,
            }))}
            defaultActiveTab={TABS.findIndex((t) => t.value === activeTab)}
            onTabChange={handleTabChange}
          />
          <div style={{ display: "flex", gap: 12 }}>
            {canViewDomainSettings && (
              <Button
                variant="outlined"
                color="default"
                onClick={() => setIsNumberSettingsOpen(true)}
              >
                {t("certificates.numberSettings.title")}
              </Button>
            )}
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Plus width={20} height={20} />}
                onClick={handleAddCertificate}
              >
                {t("certificates.addButton")}
              </Button>
            )}
          </div>
        </div>

        <CertificateNumberSettingsModal
          isOpen={isNumberSettingsOpen}
          onClose={() => setIsNumberSettingsOpen(false)}
          canUpdate={canUpdateDomainSettings}
        />

        <PageHeader
          searchPlaceholder={t("certificates.searchPlaceholder")}
          searchValue={searchInputValue}
          onSearch={setSearchInputValue}
          filters={filterFields}
          onFilterChange={handleFilterChange}
          leftExtraContent={
            <Dropdown
              onSelect={(v) => handleSortChange(Array.isArray(v) ? v[0] : v)}
              options={SORT_OPTIONS.map((opt) => ({
                label: t(`certificates.sort.${opt.value}`, getSortLabel(opt)),
                value: opt.value,
              }))}
              menuPosition="top"
              selectedLabel={
                t(
                  `certificates.sort.${currentSortValue}`,
                  getSortLabel(SORT_OPTIONS.find((o) => o.value === currentSortValue) ?? SORT_OPTIONS[0])
                )
              }
              value={currentSortValue}
            />
          }
        />

        {loading ? (
          <div className={styles.emptyState}>
            {t("common.loading")}
          </div>
        ) : certificates.length === 0 ? (
          <div className={styles.emptyState}>
            {searchTerm.trim()
              ? t("certificates.noSearchResults")
              : t("certificates.noData")}
          </div>
        ) : (
          <>
            <div className={styles.certificateGrid}>
              {certificates.map((cert) => (
                <CertificateCard
                  key={cert.id}
                  certificate={cert}
                  onActionClick={handleCertificateAction}
                />
              ))}
            </div>

            <div className={styles.paginationBar}>
              <div className={styles.paginationLeft}>
                <span className={styles.paginationLabel}>
                  {t("pagination.show")}
                </span>
                <Dropdown
                  onSelect={(v) =>
                    handlePageSizeChange(Array.isArray(v) ? v[0] : v)
                  }
                  options={PAGE_SIZE_OPTIONS.map((size) => ({
                    label: size.toString(),
                    value: size.toString(),
                  }))}
                  menuPosition="top"
                  selectedLabel={pageSize.toString()}
                  value={pageSize.toString()}
                />
                <span className={styles.paginationDetails}>
                  {t("pagination.details", {
                    total: totalRecords,
                    start: Math.min(
                      (currentPage - 1) * pageSize + 1,
                      totalRecords,
                    ),
                    end: Math.min(currentPage * pageSize, totalRecords),
                  })}
                </span>
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <ArrowLeft height={16} width={16} />
                </button>
                {renderPaginationNumbers(
                  currentPage,
                  totalPages,
                  handlePageChange,
                )}
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <ArrowRight height={16} width={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

    </DashboardLayout>
  );
};

export default CertificatesPage;
